import type { ReactNode } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { Icon as AvatarIcon } from '@/components/Custom/Avatar';
import CheckboxRow from '@/components/UI/CheckboxRow';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import getOffsetColor from '@/utils/getOffsetColor';
import getPersonDisplayName from '@/utils/getPersonDisplayName';
import AvatarColorPicker from './AvatarColorPicker';

export const normalizeUsername = (value: string) =>
  value.replace(/[^a-z0-9-]+/gi, '').toLowerCase();

type IdentityFieldsProps = {
  age?: boolean;
  avatarContent?: ReactNode;
  avatarId?: string | null;
  color: string;
  errorMessage?: string | null;
  firstName: string;
  lastName: string;
  localAvatarUrl?: string | null;
  onChangeAge?: (age: boolean) => void;
  onChangeColor(color: string): void;
  onChangeFirstName(firstName: string): void;
  onChangeLastName(lastName: string): void;
  onChangeLocalAvatarUrl(localAvatarUrl: string): void;
  onChangeUsername(username: string): void;
  requireAgeConfirmation?: boolean;
  username: string;
};

export default function IdentityFields({
  age = false,
  avatarContent,
  avatarId,
  color,
  errorMessage,
  firstName,
  lastName,
  localAvatarUrl,
  onChangeAge,
  onChangeColor,
  onChangeFirstName,
  onChangeLastName,
  onChangeLocalAvatarUrl,
  onChangeUsername,
  requireAgeConfirmation = false,
  username,
}: IdentityFieldsProps) {
  return (
    <>
      {avatarContent === undefined ? (
        <IdentityAvatarPicker
          {...{
            avatarId,
            color,
            localAvatarUrl,
            firstName,
            lastName,
            username,
            onChangeColor,
            onChangeLocalAvatarUrl,
          }}
        />
      ) : (
        avatarContent
      )}

      <Box space="md">
        <TextInput
          autoCapitalize="none"
          autoCorrect={false}
          maxLength={20}
          placeholder="Username"
          value={username}
          enterKeyHint="done"
          onChangeText={(value) => onChangeUsername(normalizeUsername(value))}
        />

        {errorMessage ? (
          <Text variant="error" center selectable>
            {errorMessage}
          </Text>
        ) : null}
      </Box>

      <Box space="md">
        <TextInput
          autoCapitalize="words"
          maxLength={35}
          placeholder="First name, optional"
          value={firstName}
          enterKeyHint="done"
          onChangeText={onChangeFirstName}
        />
        <TextInput
          autoCapitalize="words"
          maxLength={35}
          placeholder="Last name, optional"
          value={lastName}
          enterKeyHint="done"
          onChangeText={onChangeLastName}
        />
      </Box>

      {requireAgeConfirmation ? (
        <CheckboxRow
          value={age}
          onValueChange={(nextAge) => onChangeAge?.(nextAge)}
          variant="success"
          style={styles.ageConfirmationCard}
        >
          <Text bold flexShrink={1}>
            I confirm I am over the age of 13.
          </Text>
        </CheckboxRow>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  ageConfirmationCard: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    paddingHorizontal: theme.space('lg'),
    paddingVertical: theme.space('lg'),
    width: '100%',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
  },
}));

export function IdentityAvatarPicker({
  avatarId,
  color,
  localAvatarUrl,
  firstName,
  lastName,
  username,
  onChangeColor,
  onChangeLocalAvatarUrl,
}: Pick<
  IdentityFieldsProps,
  | 'avatarId'
  | 'color'
  | 'localAvatarUrl'
  | 'firstName'
  | 'lastName'
  | 'username'
  | 'onChangeColor'
  | 'onChangeLocalAvatarUrl'
>) {
  const displayName =
    getPersonDisplayName({ firstName, lastName, username }) || 'User';
  return (
    <AvatarColorPicker
      avatarId={avatarId}
      color={color}
      localAvatarUrl={localAvatarUrl}
      name={displayName}
      onChangeColor={onChangeColor}
      onChangeLocalAvatarUrl={onChangeLocalAvatarUrl}
    >
      <AvatarIcon
        name="camera-outline"
        size="5xl"
        color={getOffsetColor(color, false)}
      />
    </AvatarColorPicker>
  );
}
